import { deploy } from "./api/pages/deploy"
import path from "node:path"
import { getPagesProjectRoot, getPagesTmpDir } from "./pages/utils";
import { writeFile } from "node:fs/promises";
import { diagnoseStartupError } from "./utils/friendly-validator-errors";
import { execFileSync, execSync } from "node:child_process";
import type { Config } from "@cloudflare/workers-utils";
import { readPagesConfig } from "./config"
import {
	MAX_DEPLOYMENT_STATUS_ATTEMPTS,
	PAGES_CONFIG_CACHE_FILENAME,
} from "./pages/constants";
import { getConfigCache, saveToConfigCache } from "./config-cache";
import { getCloudflareAccountIdFromEnv } from "./user/auth-variables";
import type { PagesConfigCache } from "./pages/types";
import { requireAuth } from "./user";
import { fetchResult } from "./cfetch";
import {
	COMPLIANCE_REGION_CONFIG_PUBLIC,
	configFileName,
	FatalError,
	findWranglerConfig,
	ParseError,
	UserError,
} from "@cloudflare/workers-utils";

import type {
	Deployment,
	DeploymentStage,
	Project,
	UnifiedDeploymentLogMessages,
} from "@cloudflare/types";


type Options = {
	projectName: string;
	productionBranch: string;
	branch: string;
}


async function run(directory: string, options: Options) {
	if (!directory) throw new Error("No directory provided")
	if (!options.projectName) throw new Error("No project name.")

	let config: Config | undefined

	const { configPath } = findWranglerConfig(process.cwd(), {
		useRedirectIfAvailable: true,
	});

	try {
		config = readPagesConfig({ config: configPath, env: undefined });
	} catch (e) {
		console.log(e)
	}


	const configCache = getConfigCache<PagesConfigCache>(
		PAGES_CONFIG_CACHE_FILENAME
	);


	const accountId =
		getCloudflareAccountIdFromEnv() ?? (await requireAuth(configCache));


	let projectName = options.projectName ?? config?.name ?? configCache.project_name;
	let isExistingProject = true;



	if (projectName) {
		try {
			await fetchResult<Project>(
				COMPLIANCE_REGION_CONFIG_PUBLIC,
				`/accounts/${accountId}/pages/projects/${projectName}`
			);
		} catch (err) {
			// code `8000007` corresponds to project not found
			if ((err as { code: number }).code !== 8000007) {
				throw err;
			} else {
				isExistingProject = false;
			}
		}
	}

	let isGitDir = true
	try {
		execSync(`git rev-parse --is-inside-work-tree`, {
			stdio: "ignore",
		});
	} catch (err) {
		isGitDir = false;
	}

	let productionBranch: string | undefined = options.productionBranch;

	if (isGitDir && !productionBranch) {
		try {
			productionBranch = execSync(`git rev-parse --abbrev-ref HEAD`)
				.toString()
				.trim();
		} catch (err) {
			null
		}
	}

	if (!productionBranch) throw new Error("Missing production branch")

	if (!isExistingProject) {
		// Create a new project

		await fetchResult<Project>(
			COMPLIANCE_REGION_CONFIG_PUBLIC,
			`/accounts/${accountId}/pages/projects`,
			{
				method: "POST",
				body: JSON.stringify({
					name: projectName,
					production_branch: productionBranch,
				}),
			}
		);

		saveToConfigCache<PagesConfigCache>(PAGES_CONFIG_CACHE_FILENAME, {
			account_id: accountId,
			project_name: projectName,
		});
	}

	let isGitDirty = false;

	let branch

	if (isGitDir) {
		try {
			const statusOutput = execSync(`git status --porcelain`).toString();
			isGitDirty = Boolean(statusOutput.length);

			if (!branch) {
				branch = execSync(`git rev-parse --abbrev-ref HEAD`)
					.toString()
					.trim();
			} else {
				// Using provided branch
			}

			let commitHash = execSync(`git rev-parse HEAD`).toString().trim();

			let commitMessage = execFileSync("git", [
				"show",
				"-s",
				"--format=%B",
				commitHash,
			])
				.toString()
				.trim();
		} catch (err) {
			throw err
		}

	}


	const { deploymentResponse, formData } = await deploy({
		directory,
		accountId,
		projectName,
		branch,
		// Sourcemaps from deploy arguments will take precedence so people can try it for one-off deployments without updating their wrangler.toml
		sourceMaps: false
	});


	saveToConfigCache<PagesConfigCache>(PAGES_CONFIG_CACHE_FILENAME, {
		account_id: accountId,
		project_name: projectName,
	});



	let latestDeploymentStage: DeploymentStage | undefined;
	let alias: string | undefined;
	let attempts = 0;

	while (
		attempts < MAX_DEPLOYMENT_STATUS_ATTEMPTS &&
		latestDeploymentStage?.name !== "deploy" &&
		latestDeploymentStage?.status !== "success" &&
		latestDeploymentStage?.status !== "failure"
	) {
		try {
			/*
			 * Exponential backoff
			 * On every retry, exponentially increase the wait time: 1 second, then
			 * 2s, then 4s, then 8s, etc.
			 */
			await new Promise((resolvePromise) =>
				setTimeout(resolvePromise, Math.pow(2, attempts++) * 1000)
			);

			const deployment = await fetchResult<Deployment>(
				COMPLIANCE_REGION_CONFIG_PUBLIC,
				`/accounts/${accountId}/pages/projects/${projectName}/deployments/${deploymentResponse.id}`
			);
			latestDeploymentStage = deployment.latest_stage;
			// Aliases is an array but will only ever return one pages.dev
			// If preview, this will return a branch alias. If production, this will return custom domains
			alias = (deployment.aliases?.filter((a) => a.endsWith(".pages.dev")) ??
				[])[0];
		} catch (err) {
			// don't retry if API call retruned an error
			throw err
		}
	}


	if (
		latestDeploymentStage?.name === "deploy" &&
		latestDeploymentStage?.status === "success"
	) {
		null
	} else if (
		latestDeploymentStage?.name === "deploy" &&
		latestDeploymentStage?.status === "failure"
	) {
		// get persistent logs so we can show users the failure message
		const logs = await fetchResult<UnifiedDeploymentLogMessages>(
			COMPLIANCE_REGION_CONFIG_PUBLIC,
			`/accounts/${accountId}/pages/projects/${projectName}/deployments/${deploymentResponse.id}/history/logs?size=10000000`
		);
		// last log entry will be the most relevant for Direct Uploads
		const failureMessage = logs.data[logs.total - 1].line
			.replace("Error:", "")
			.trim();

		if (failureMessage.includes("Script startup exceeded CPU time limit")) {
			const startupError = new ParseError({
				text: failureMessage,
				telemetryMessage: false,
			});
			Object.assign(startupError, { code: 10021 }); // Startup error code
			const workerBundle = formData.get("_worker.bundle") as File;
			const filePath = path.join(getPagesTmpDir(), "_worker.bundle");
			await writeFile(filePath, workerBundle.stream());
			throw new UserError(
				await diagnoseStartupError(
					startupError,
					filePath,
					getPagesProjectRoot()
				),
				{ telemetryMessage: "pages deploy startup error" }
			);
		}

		throw new FatalError(
			`Deployment failed!
	${failureMessage}`,
			{ code: 1, telemetryMessage: "pages deploy deployment failed" }
		);
	} else {
		// Deployment complete
	}
}

export default run